import React, { useEffect, useState } from 'react'
import { Row, Col } from 'react-bootstrap'
import { useGetModelQuery, useStopTrainModelMutation } from '../../../services/modelsApi'
import { useUpdateDatasetMutation } from '../../../services/datasetApi'
import { useAppDispatch, useAppSelector, useAnnotationStats } from '../../../store/hooks'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import { useClassDistribution } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import {
  selectSelectedTrainingSource,
  selectSelectedValidationSource,
  setTrainingSource,
  setValidationSource,
} from '../../../store/slices/datasetSlice'
import ConfirmDialog from '../../modals/ConfirmDialog'
import ErrorDialog from '../../modals/ErrorDialog'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { ModelInfoCard, DatasetSourcesCard, DatasetStatsCard } from './components'

const ModelGenerationView: React.FC = () => {
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const selectedTrainingSources = useAppSelector(selectSelectedTrainingSource)
  const validationSources = useAppSelector(selectSelectedValidationSource)
  const selectedModelId = useAppSelector(selectSelectedModelId)

  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  const [updateDataset] = useUpdateDatasetMutation()
  const [stopTrainModel] = useStopTrainModelMutation()

  const [showStopTrainingDialog, setShowStopTrainingDialog] = useState(false)

  const [trainSplit, setTrainSplit] = useState(80) // Default to 80% train, 20% validation
  const [shouldSplit, setShouldSplit] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  const classDistribution = useClassDistribution(
    selectedProjectId || '',
    project?.datasetid || '',
    // false,
    selectedTrainingSources
  )

  const { data: annoStats } = useAnnotationStats(
    selectedProjectId || '',
    project?.datasetid || '',
    selectedTrainingSources
  )

  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Add this function to validate datasets
  useEffect(() => {
    // Only check if both arrays have items
    if (selectedTrainingSources?.length && validationSources?.length) {
      // Check if there's at least one matching dataset
      const hasMatchingDataset = validationSources.some((source) =>
        selectedTrainingSources.includes(source)
      )

      // Show split slider if there's at least one matching dataset
      setShouldSplit(hasMatchingDataset)
    } else {
      // If either array is empty, don't show split slider
      setShouldSplit(false)
    }
  }, [selectedTrainingSources, validationSources])

  const handleSplitChange = (value: number) => {
    setTrainSplit(value)
  }

  const handleSplitChangeComplete = async () => {
    if (selectedProjectId && project?.datasetid) {
      const datasetSplit = {
        test: 0,
        train: trainSplit / 100,
        validation: (100 - trainSplit) / 100,
      }

      try {
        await updateDataset({
          Id: project.datasetid,
          split: datasetSplit,
        }).unwrap()
      } catch (err: unknown) {
        setErrorMessage(
          (err as { data?: { message: string } })?.data?.message || 'Failed to update dataset split'
        )
        setShowError(true)
      }
    }
  }

  // Add effect to clear datasets when model changes or is deleted
  useEffect(() => {
    // Clear selected datasets when model changes
    dispatch(setTrainingSource(selectedModel?.parameters?.train_metadata?.datasource || []))
    dispatch(setValidationSource(selectedModel?.parameters?.validation_metadata?.datasource || []))
    setTrainSplit(80)
    setSelectedTagIds([])
  }, [
    dispatch,
    selectedModel?.id,
    selectedModel?.parameters?.train_metadata?.datasource,
    selectedModel?.parameters?.validation_metadata?.datasource,
  ]) // Only trigger when model ID changes

  const handleStopTraining = async () => {
    if (selectedModel?.id) {
      try {
        const stoppedModel = await stopTrainModel({
          id: selectedModel.id,
        }).unwrap()

        // No need to set selected model

        // Close the dialog
        setShowStopTrainingDialog(false)
      } catch (err: any) {
        setErrorMessage(err?.data?.message || 'Failed to stop model training')
        setShowError(true)
      }
    }
  }

  return (
    <div className="model-metrics">
      <Row className="g-4">
        <Col md={4}>
          <ModelInfoCard model={selectedModel} />
        </Col>

        <Col md={4}>
          <DatasetSourcesCard
            selectedTagIds={selectedTagIds}
            model={selectedModel}
            trainSplit={trainSplit}
            shouldSplit={shouldSplit}
            onSplitChange={handleSplitChange}
            onSplitChangeComplete={handleSplitChangeComplete}
            onTagSelectionChange={setSelectedTagIds}
          />
        </Col>

        <Col md={4}>
          <DatasetStatsCard
            selectedTrainingSources={selectedTrainingSources || []}
            classDistribution={classDistribution}
            annoStats={annoStats}
          />
        </Col>
      </Row>

      <ConfirmDialog
        show={showStopTrainingDialog}
        onHide={() => setShowStopTrainingDialog(false)}
        onConfirmRight={handleStopTraining}
        title="Stop Training"
        message="Are you sure you want to stop the model training? This action cannot be undone."
        confirmRightText="Stop Training"
        cancelText="Cancel"
        variant="danger"
      />

      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage}
        title="Model Training Error"
      />
    </div>
  )
}

export default ModelGenerationView
